import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, BookOpen, Library, Activity, Zap } from 'lucide-react';
import { useState, useEffect } from 'react';
import config from '../../data/site_config.json';
import { parseMarkdownItalics } from '../SafecareBrand';
import './Header.css';

// static import removed

// Generic Dropdown Component
const NavDropdown = ({ title, items, activeBase, icon: Icon }) => {
    const location = useLocation();
    const isActive = activeBase ? location.pathname.startsWith(activeBase) : items.some(i => location.pathname === i.path);

    // Filter items based on activeBase logic if they don't supply active checks
    return (
        <div className="nav-item-dropdown-wrapper">
            <button className={`nav-link dropdown-trigger ${isActive ? 'active' : ''}`}>
                {Icon && <Icon size={18} className="nav-icon" />}
                {title}
                <ChevronDown size={14} className="dropdown-arrow" />
                {isActive && <motion.div layoutId="underline" className="active-underline" />}
            </button>
            <div className="nav-dropdown-menu simple-dropdown">
                {items.map((item) => (
                    item.external ? (
                        <a
                            key={item.href}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="nav-dropdown-item"
                        >
                            <span className="dropdown-series-title">{item.label}</span>
                            {item.subtitle && <span className="dropdown-series-subtitle">{item.subtitle}</span>}
                        </a>
                    ) : (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                        >
                            <span className="dropdown-series-title">{item.label}</span>
                            {item.subtitle && <span className="dropdown-series-subtitle">{item.subtitle}</span>}
                        </Link>
                    )
                ))}
            </div>
        </div>
    );
};

// Shock Dropdown (Specific Series Logic)
const ShockDropdown = () => {
    const [shockData, setShockData] = useState(null);

    useEffect(() => {
        fetch('/data/shock_therapy_content.json')
            .then(res => res.json())
            .then(data => setShockData(data))
            .catch(console.error);
    }, []);

    if (!shockData) return null;

    return (
        <div className="nav-dropdown-menu shock-dropdown">
            {shockData.series.map((series) => (
                <Link
                    to={`/shock-therapy#${series.id}`}
                    key={series.id}
                    className="nav-dropdown-item"
                    onClick={() => {
                        if (window.location.pathname === '/shock-therapy') {
                            const el = document.getElementById(series.id);
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                >
                    <span className="dropdown-series-title">{series.title}</span>
                    <span className="dropdown-series-subtitle">{series.subtitle}</span>
                </Link>
            ))}
        </div>
    );
};

export default function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    return (
        <header className={`header ${scrolled ? 'scrolled' : ''}`}>
            <div className="container header-content">
                <Link to="/" className="logo">
                    <img src="/image/safecarelogo.png" alt="SAFEC+ Logo" className="logo-image" />
                    <span>{parseMarkdownItalics("SAFEC+")}</span>
                </Link>

                {/* Desktop Nav */}
                <nav className="desktop-nav">

                    {/* 1. THE FRAMEWORK */}
                    <NavDropdown
                        title="The Framework"
                        icon={BookOpen}
                        items={[
                            { label: "The Plan", path: "/plan", subtitle: "Core Strategy" },
                            { label: "How it Works", path: "/how-it-works", subtitle: "The SAFECARE Mechanism" },
                            { label: "Infrastructure", path: "/infrastructure", subtitle: "A Pragmatic Reset" },
                            { label: "The Bill", path: "/the-bill", subtitle: "Legislative Text" }
                        ]}
                    />

                    {/* 2. RESOURCES (Replaced Analysis) */}
                    <NavDropdown
                        title="Resources"
                        icon={Library}
                        items={[
                            { label: "Knowledge Base", path: "/issues", subtitle: "Encyclopedia of Reform" },
                            { label: "Myth vs Reality", path: "/myth-vs-reality", subtitle: "Rebuttals & FAQs" }
                        ]}
                    />

                    {/* 3. TOOLS */}
                    <NavDropdown
                        title="Tools"
                        icon={Activity}
                        items={[
                            { label: "Calculator", path: "/calculator", subtitle: "Individual Impact" },
                            { label: "Simulator", path: "/simulator", subtitle: "National Impact" },
                            { label: "Vote Tracker", href: "https://vote.safecplus.org", subtitle: "House & Senate Likelihoods", external: true },
                            { label: "Health Map", href: "https://healthmap.safecplus.org", subtitle: "SAFEC+ vs M4A vs Current", external: true }
                        ]}
                    />

                    {/* 4. SHOCK THERAPY (Special Dropdown) */}
                    <div className="nav-item-dropdown-wrapper">
                        <Link to="/shock-therapy" className={`nav-link ${location.pathname.startsWith('/shock-therapy') ? 'active' : ''}`}>
                            <Zap size={18} className="nav-icon" />
                            Shock Therapy
                            {location.pathname.startsWith('/shock-therapy') && <motion.div layoutId="underline" className="active-underline" />}
                        </Link>
                        <ShockDropdown />
                    </div>

                    {/* <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>
                        Contact
                        {location.pathname === '/contact' && <motion.div layoutId="underline" className="active-underline" />}
                    </Link> */}
                </nav>

                {/* Mobile Toggle */}
                <button className="mobile-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle navigation menu">
                    {isOpen ? <X /> : <Menu />}
                </button>

                {/* Mobile Nav */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="mobile-nav"
                            initial={{ opacity: 0, x: "100%" }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: "100%" }}
                            transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                        >
                            <div className="mobile-nav-content">
                                {config.navigation.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
}
